import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { HbrIcon } from '@harbor/elements-react-standalone';
import { x } from '@harbor/elements/icons';
import StbButton, { StbButtonRole } from '../../../src/components/security-toolbox-ui/StbButton';
import { Z_INDEX_MODAL, MODAL_FADE_TIMER_MS } from './security-toolbox-ui-const';
import { createPortal } from 'react-dom';
import { useModal } from './StbModalContext';


export type StbModalSize = 'small'|'medium'|'large'|'x-large';
export type StbModalRole = 'save'|'delete'|'update'|'information'|'default';

const modalRole: Record<
  StbModalRole,
  {
    submitLabel?: string;
    submitButtonRole?: StbButtonRole;
    hideCancel?: boolean;
    hideHeader?: boolean;
    size?: StbModalSize;
  }
> = {
  delete: {
    hideHeader: true,
    submitButtonRole: 'delete',
  },
  save: {
    hideHeader: true,
    submitButtonRole: 'save',
  },
  update: {
    hideHeader: true,
    submitButtonRole: 'update',
  },
  information: {
    hideHeader: true,
    hideCancel: true,
    submitButtonRole: 'default',
  },
  default: {}
};

export interface StbModalProps {
  children: ReactNode;
  id?: string;
  open: boolean;
  role?: StbModalRole;
  title?: ReactNode;
  size?: StbModalSize;
  scrollable?: boolean;
  submitLabel?: string;
  submitIcon?: () => string;
  submitButtonRole?: StbButtonRole;
  submitDisabled?: boolean;
  cancelLabel?: string;
  hideHeader?: boolean;
  hideCancel?: boolean;
  onClose: () => Promise<void>;
  onSubmit?: () => Promise<void>;
  suppressCloseOnSubmit?: boolean;
  apiResponseSection?: ReactNode;
}

const StbModal: React.FC<StbModalProps> = ({
  children,
  id,
  open,
  role = 'default',
  title,
  size,
  scrollable = false,
  submitLabel,
  submitIcon = null,
  submitButtonRole,
  submitDisabled,
  cancelLabel,
  hideHeader,
  hideCancel,
  onClose,
  onSubmit = async () => Promise.resolve(),
  suppressCloseOnSubmit = false,
  apiResponseSection = undefined,
}: StbModalProps) => {
  const { registerModal, unregisterModal, getModalIndex, resetModalIndex } = useModal();

  const modalId = useRef<string>(`stb-modal-${Math.random().toString(36).substr(2, 9)}`);
  const isMounted = useRef(true);
  useEffect(() => {
    const currentModalId = modalId.current;
    isMounted.current = true;
    registerModal(currentModalId);
    return () => {
      isMounted.current = false;
      unregisterModal(currentModalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [modalIdx, setModalIdx] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (open) {
      setInitialized(true);
      setModalIdx(getModalIndex(modalId.current));
      setTimeout(() => setVisible(true), 10);
    } else if (initialized) {
      resetModalIndex(modalId.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [submitting, setSubmitting] = useState(false);

  const _size = size ?? modalRole[role]?.size ?? 'medium';
  const _hideHeader = hideHeader ?? (modalRole[role]?.hideHeader && (!title || title.toString().trim() === '')) ?? false;
  const _hideCancel = hideCancel ?? modalRole[role]?.hideCancel ?? false;

  const zLevelOffset = modalIdx >= 0 ? Z_INDEX_MODAL + modalIdx * 100 : 0;

  const _onClose = async (e: MouseEvent) => {
    e.stopPropagation();
    setVisible(false);
    setTimeout(async () => {
      if (isMounted.current) {
        await onClose();
      }
    }, MODAL_FADE_TIMER_MS);
  };

  const _onSubmit = async (e: MouseEvent) => {
    e.stopPropagation();
    setSubmitting(true);
    try {
      await onSubmit();
      if (!suppressCloseOnSubmit) {
        setVisible(false);
        setTimeout(async () => {
          if (isMounted.current) {
            await onClose();
          }
        }, MODAL_FADE_TIMER_MS);
      }
    } finally {
      if (isMounted.current) {
        setSubmitting(false);
      }
    }
  };

  const _submitLabel = submitLabel ?? (role ? modalRole[role]?.submitLabel : 'Close');
  const _submitButtonRole = role === 'default'
    ? submitButtonRole ?? 'default'
    : role ? modalRole[role]?.submitButtonRole : 'default';

  const _cancelLabel = cancelLabel ?? 'Cancel';

  if (!open) {
    return null;
  }

  return createPortal(
    <>
      <div
        onClick={submitting ? undefined : _onClose}
        style={{ zIndex: zLevelOffset }}
        className={`stb-modal-background ${visible ? 'visible' : 'not-visible'}`}
      ></div>

      <section
        id={id ?? modalId.current}
        style={{ zIndex: zLevelOffset + 50 }}
        className={`stb-modal stb-modal--size-${_size} ${visible ? 'visible' : 'not-visible'}`}
      >
        <HbrIcon
          asset={x}
          onClick={submitting ? undefined : _onClose}
          className={`stb-modal--x ${submitting ? 'disabled' : ''}`}  
        />
        {_hideHeader
          ? null
          : title && (
            <header className="stb-modal--header">
              <h2>{title}</h2>
            </header>
          )
        }

        <div className={`stb-modal--content ${scrollable ? 'scrollable' : ''}`}>
          <div className="stb-modal--content-inner">
            {children}
          </div>
        </div>

        {apiResponseSection && (
          <div className="stb-modal--api-response-section">
            {apiResponseSection}
          </div>
        )}

        <footer className="stb-modal--footer">
          {!_hideCancel && (
            <StbButton
              role="cancel"
              onClick={async (e) => await _onClose(e)}
              disabled={submitting}
              className="stb-modal--cancel-button"
            >
              {_cancelLabel}
            </StbButton>
          )}
          <StbButton
            role={_submitButtonRole}
            onClick={async (e) => await _onSubmit(e)}
            disabled={submitDisabled || submitting}
            loading={submitting}
            className="stb-modal--close-button"
            icon={submitIcon}
          >
            {_submitLabel}
          </StbButton>
        </footer>
      </section>
    </>,
    document.getElementById('stb-app-modal') as HTMLElement
  )
};

export default StbModal;
